import { z } from 'zod'

// ─── Node Types ───

export const NodeType = z.enum([
  'ROOT',
  'AND',
  'OR',
  'FILTER',
  'SLICE_GROUP',
  'CONDITIONAL_AGG',
  'VISUAL_GROUP',
])
export type NodeType = z.infer<typeof NodeType>

/** Categories for render branching. */
export const NodeCategory = z.enum(['group', 'entity'])
export type NodeCategory = z.infer<typeof NodeCategory>

export const GROUP_NODE_TYPES: NodeType[] = [
  'ROOT',
  'AND',
  'OR',
  'SLICE_GROUP',
  'CONDITIONAL_AGG',
  'VISUAL_GROUP',
]

export const ENTITY_NODE_TYPES: NodeType[] = ['FILTER']

export function isGroupNode(type: NodeType): boolean {
  return GROUP_NODE_TYPES.includes(type)
}

// ─── TreeNode (discriminated by type) ───

const BaseNode = z.object({
  id: z.string(),
  parentId: z.string().nullable(),
})

export const GroupNodeSchema = BaseNode.extend({
  type: z.enum(['ROOT', 'AND', 'OR', 'SLICE_GROUP', 'CONDITIONAL_AGG', 'VISUAL_GROUP']),
  children: z.array(z.string()),
})

const EntityNodeSchema = BaseNode.extend({
  type: z.literal('FILTER'),
})

export const TreeNodeSchema = z.discriminatedUnion('type', [
  BaseNode.extend({ type: z.literal('ROOT'), children: z.array(z.string()) }),
  BaseNode.extend({ type: z.literal('AND'), children: z.array(z.string()) }),
  BaseNode.extend({ type: z.literal('OR'), children: z.array(z.string()) }),
  BaseNode.extend({ type: z.literal('SLICE_GROUP'), children: z.array(z.string()) }),
  BaseNode.extend({ type: z.literal('CONDITIONAL_AGG'), children: z.array(z.string()) }),
  BaseNode.extend({ type: z.literal('VISUAL_GROUP'), children: z.array(z.string()) }),
  EntityNodeSchema,
])

export type TreeNode = z.infer<typeof TreeNodeSchema>
export type GroupNode = z.infer<typeof GroupNodeSchema>
export type EntityNode = z.infer<typeof EntityNodeSchema>
